/**
 * A snapshot compared with today.
 *
 * The changes lead; the timeline is a disclosure beneath them. That is the
 * inversion of the original design, which opened straight onto two full Gantt
 * charts and left "what actually changed" to be found by scanning them — with
 * the answer already computed and thrown away.
 *
 * One chart, not two. `SnapshotCompareTimeline` explains why.
 */
import { useMemo, useState } from "react";

import { cn } from "@/lib/utils";
import { UnderlineTabs } from "@/components/ui/underline-tabs";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { fill } from "@/lib/i18n/fill";
import { useIssuesStore } from "@/stores/pm/hooks/usePMStore";
import type { ScheduleSnapshot } from "@/domains/pm/types";
import type { GanttGranularity } from "@/domains/pm/utils/ganttScale";
import {
  compareRows,
  diffSnapshot,
  isUnchanged,
  type SnapshotChangeKind,
} from "@/domains/pm/utils/scheduleSnapshotDiff";
import { formatDateOnly } from "@/domains/pm/utils/protocol";
import { snapshotDay, snapshotName } from "./snapshotName";
import { GanttGranularityControl } from "@/domains/pm/components/issues/gantt/GanttGranularityControl";
import {
  SnapshotChangeCounts,
  SnapshotChangeList,
  SnapshotCompletionShift,
} from "./SnapshotChangeList";
import { SnapshotCompareTimeline } from "./SnapshotCompareTimeline";
import { COMPARE_BAR } from "./snapshotDiffTones";

export function SnapshotCompareDialog({
  open,
  onOpenChange,
  snapshot,
  selectedIssueId,
  onSelectIssue,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  snapshot: ScheduleSnapshot;
  selectedIssueId?: number;
  onSelectIssue?: (issueId: number) => void;
}) {
  const { t, language } = useLanguage();
  const copy = t.pm.issues.gantt;
  const issuesStore = useIssuesStore();
  const [granularity, setGranularity] = useState<GanttGranularity>("week");
  /** Timeline leads: the chart is what the reader came to see, and the list
   *  behind it answers "which ones" when the chart raises the question. */
  const [tab, setTab] = useState<"timeline" | "overview">("timeline");
  /** Unchanged rows are hidden until asked for: on a real project almost every
   *  issue is unchanged, and three moves among two hundred is not findable. */
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [kinds, setKinds] = useState<Set<SnapshotChangeKind>>(new Set());
  const [focused, setFocused] = useState<number | undefined>(selectedIssueId);

  const diff = useMemo(
    () => (snapshot.payload ? diffSnapshot(snapshot.payload, issuesStore.issues) : null),
    [snapshot, issuesStore.issues]
  );

  const rows = useMemo(() => {
    if (!diff) return [];
    return compareRows(diff).filter((r) => {
      if (kinds.size > 0) return kinds.has(r.kind);
      return showUnchanged || r.kind !== "unchanged";
    });
  }, [diff, kinds, showUnchanged]);

  const name = snapshotName(snapshot, language);
  const total = diff ? diff.entries.length : 0;

  const toggleKind = (kind: SnapshotChangeKind) =>
    setKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* gap-0 and tighter padding than a default dialog: the chart is the
          content, and every pixel of chrome comes out of its height. */}
      <DialogContent className="flex h-[90vh] max-h-[90vh] w-[95vw] max-w-[95vw] flex-col gap-0 overflow-hidden p-4 pb-0">
        <DialogHeader className="flex-shrink-0 space-y-1.5 border-b">
          {/* Title left, counts in the top middle; the right column only
              balances the grid so the counts stay centred (and keeps them
              clear of the close button). */}
          <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-4 pr-8">
            <div className="min-w-0 space-y-0.5">
              <DialogTitle className="text-base font-medium">{copy.compareTitle}</DialogTitle>
              <p className="text-xs text-muted-foreground">
                {name} · {snapshotDay(snapshot, language)}
              </p>
              {diff && <SnapshotCompletionShift diff={diff} />}
            </div>
            {diff && !isUnchanged(diff) ? (
              <SnapshotChangeCounts diff={diff} active={kinds} onToggleKind={toggleKind} />
            ) : (
              <span />
            )}
            <span aria-hidden="true" />
          </div>
          {diff && !isUnchanged(diff) && (
            <div className="flex flex-wrap items-end justify-between gap-3">
              {/* -mb-px so the strip's rule lands on the header's own bottom
                  border, the way the page's view tabs do. */}
              <UnderlineTabs
                className="-mb-px"
                value={tab}
                onValueChange={(v: "timeline" | "overview") => setTab(v)}
                options={[
                  { value: "timeline", label: copy.compareTabTimeline },
                  { value: "overview", label: copy.compareTabOverview },
                ]}
              />
              <div className="flex items-center gap-3 pb-1">
                {/* One swatch per lane, top to bottom in the chart's order. */}
                {tab === "timeline" && (
                  <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <Swatch fill={COMPARE_BAR.baseline} /> {copy.compareLegendBaseline}
                    <Swatch fill={COMPARE_BAR.today} className="ml-2" /> {copy.compareLegendToday}
                  </span>
                )}
                {tab === "timeline" && (
                  <GanttGranularityControl
                    value={granularity}
                    onChange={setGranularity}
                    tone="brand"
                  />
                )}
                <div className="flex items-center gap-2">
                  <Label htmlFor="compare-unchanged" className="cursor-pointer text-xs">
                    {copy.compareShowUnchanged}
                  </Label>
                  <Switch
                    id="compare-unchanged"
                    checked={showUnchanged}
                    onCheckedChange={setShowUnchanged}
                    disabled={kinds.size > 0}
                  />
                </div>
              </div>
            </div>
          )}
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {diff && isUnchanged(diff) ? (
            <SnapshotChangeList diff={diff} rows={[]} />
          ) : (
            <>
              {/* Each tab fills the body. Neither is a slice of the other any
                  more — the timeline used to be a disclosure at the foot, so
                  opening it left the chart in a 55% band under a list nobody
                  was reading. */}
              {tab === "timeline" && (
                <SnapshotCompareTimeline
                  rows={rows}
                  granularity={granularity}
                  selectedIssueId={focused}
                  onSelectIssue={(id) => {
                    setFocused(id);
                    onSelectIssue?.(id);
                  }}
                />
              )}
              {tab === "overview" && diff && (
                <div className="min-h-0 flex-1 overflow-y-auto px-1 py-3">
                  <SnapshotChangeList
                    diff={diff}
                    rows={rows}
                    selectedIssueId={focused}
                    onSelectIssue={(id) => {
                      setFocused(id);
                      onSelectIssue?.(id);
                    }}
                  />
                </div>
              )}

              <p className="border-t px-1 py-1 text-[11px] text-muted-foreground">
                {fill(copy.compareShowing, {
                  shown: String(rows.length),
                  total: String(total),
                })}
              </p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Swatch({ fill, className }: { fill: string; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn("inline-block h-2.5 w-4 shrink-0 rounded-sm", className)}
      style={{ backgroundColor: fill }}
    />
  );
}
